import React, { useRef, useState, useEffect, useCallback } from 'react';
import { LayoutGrid, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CategoryFilterItem {
  id: string;
  name: string;
  color?: string;
  /** Cuando se indica, la pill activa usa el fondo sólido del token semántico correspondiente en vez del azul de marca o el color custom. */
  tone?: 'success' | 'error';
  productCount?: number;
}

export interface CategoryFilterProps {
  categories: CategoryFilterItem[];
  activeIds: Set<string>;
  onChange: (activeIds: Set<string>) => void;
  className?: string;
  /** 'multiple' (default) permite combinar varias categorías activas a la vez, como en el filtro por categoría del Punto de Venta. 'single' hace que las opciones sean excluyentes entre sí, como pestañas de filtro. */
  mode?: 'multiple' | 'single';
  /** Texto de la pill que representa "sin filtro". */
  allLabel?: string;
  /** Etiqueta previa a las pills (ej. "Categorías"). Pasa '' para ocultarla. */
  label?: string;
  /** Icono de la pill "todo". Pasa null para no mostrar ninguno. */
  icon?: React.ComponentType<{ className?: string }> | null;
}

function buildPillActiveStyle(color: string): React.CSSProperties {
  return { '--pill-color': color } as React.CSSProperties;
}

export const CategoryFilter: React.FC<CategoryFilterProps> = ({
  categories,
  activeIds,
  onChange,
  className,
  mode = 'multiple',
  allLabel = 'Todos',
  label = 'Categorías',
  icon: Icon = LayoutGrid,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);
  const suppressClickRef = useRef(false);

  const allActive = activeIds.size === 0 || activeIds.has('all');

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 1);
    setShowRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateFades();
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    el.addEventListener('scroll', updateFades, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', updateFades);
    };
  }, [categories, updateFades]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaX !== 0 || e.deltaY === 0) return;
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || e.pointerType !== 'mouse' || e.button !== 0) return;
    dragRef.current = { startX: e.clientX, startScrollLeft: el.scrollLeft };
    el.setPointerCapture(e.pointerId);
    setIsDragging(true);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    const drag = dragRef.current;
    if (!el || !drag) return;
    const delta = e.clientX - drag.startX;
    if (Math.abs(delta) > 4) suppressClickRef.current = true;
    el.scrollLeft = drag.startScrollLeft - delta;
  }, []);

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (el?.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  const scrollByStep = useCallback((direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.75, behavior: 'smooth' });
  }, []);

  const handlePillClick = useCallback(
    (id: string) => {
      if (id === 'all') {
        onChange(new Set());
        return;
      }
      if (mode === 'single') {
        onChange(new Set([id]));
        return;
      }
      const next = new Set(activeIds);
      next.delete('all');
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onChange(next.size === 0 ? new Set() : next);
    },
    [activeIds, onChange, mode],
  );

  return (
    <div className={cn('flex flex-col gap-0', className)}>
      <div className="relative">
        <div
          ref={scrollRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={endDrag}
          onClickCapture={handleClickCapture}
          className={cn(
            'flex items-center gap-1.5 overflow-x-auto px-[1px] pt-[1px] pb-3 scrollbar-none',
            isDragging ? 'cursor-grabbing select-none' : 'cursor-grab',
            showLeftFade && 'pl-9',
            showRightFade && 'pr-9',
          )}
          style={{ scrollbarWidth: 'none' }}
        >
          {label && (
            <span className="text-xs font-medium text-secondary whitespace-nowrap flex-shrink-0 pr-1">
              {label}
            </span>
          )}
          <button
            type="button"
            onClick={() => handlePillClick('all')}
            aria-pressed={allActive}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full',
              'border text-xs font-medium whitespace-nowrap flex-shrink-0',
              'transition-all duration-150 select-none active:scale-[0.97]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
              'bg-surface-2 border-default text-secondary',
              !allActive && 'hover:text-primary hover:bg-surface-0',
              allActive && 'bg-brand text-inverse',
            )}
          >
            {Icon && <Icon className="w-3 h-3" aria-hidden="true" />}
            {allLabel}
            <span
              className={cn(
                'text-[10.5px] font-medium px-1.5 py-px rounded-full',
                allActive ? 'bg-white/20' : 'bg-black/[0.06]',
              )}
            >
              {categories.reduce((sum, c) => sum + (c.productCount ?? 0), 0)}
            </span>
          </button>

          {categories.map((cat) => {
            const isActive = !allActive && activeIds.has(cat.id);
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => handlePillClick(cat.id)}
                aria-pressed={isActive}
                style={isActive && cat.color ? buildPillActiveStyle(cat.color) : undefined}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full',
                  'border text-xs font-medium whitespace-nowrap flex-shrink-0',
                  'transition-all duration-150 select-none active:scale-[0.97]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                  'bg-surface-2 border-default text-secondary',
                  !isActive && 'hover:text-primary hover:bg-surface-0',
                  isActive && cat.tone === 'success' && 'bg-[var(--success)] text-[var(--text-inverse)]',
                  isActive && cat.tone === 'error' && 'bg-[var(--error)] text-[var(--text-inverse)]',
                  isActive && !cat.tone && cat.color && [
                    'border-[var(--pill-color)]',
                    'text-[var(--pill-color)]',
                    'bg-[color-mix(in_srgb,var(--pill-color)_10%,transparent)]',
                  ],
                  isActive && !cat.tone && !cat.color && 'bg-brand text-inverse',
                )}
              >
                {cat.color && (
                  <span
                    className="w-[7px] h-[7px] rounded-full flex-shrink-0"
                    style={{ background: cat.color }}
                    aria-hidden="true"
                  />
                )}
                {cat.name}
                {cat.productCount !== undefined && (
                  <span
                    className={cn(
                      'text-[10.5px] font-medium px-1.5 py-px rounded-full',
                      !isActive && 'bg-black/[0.06]',
                      isActive && cat.color && 'bg-[color-mix(in_srgb,var(--pill-color)_15%,transparent)]',
                      isActive && !cat.color && 'bg-white/20',
                    )}
                  >
                    {cat.productCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {showLeftFade && (
          <div
            className="absolute left-0 top-0 bottom-3 w-9 flex items-center justify-center
                        bg-gradient-to-l from-transparent to-surface-0"
          >
            <button
              type="button"
              onClick={() => scrollByStep(-1)}
              aria-label="Desplazar categorías a la izquierda"
              className="flex items-center justify-center w-6 h-6 rounded-full bg-surface-0 border border-default
                         shadow-sm text-secondary hover:text-primary hover:bg-surface-hover transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {showRightFade && (
          <div
            className="absolute right-0 top-0 bottom-3 w-9 flex items-center justify-center
                        bg-gradient-to-r from-transparent to-surface-0"
          >
            <button
              type="button"
              onClick={() => scrollByStep(1)}
              aria-label="Desplazar categorías a la derecha"
              className="flex items-center justify-center w-6 h-6 rounded-full bg-surface-0 border border-default
                         shadow-sm text-secondary hover:text-primary hover:bg-surface-hover transition-colors"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryFilter;


